import { HttpClient } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import {
    ChartDataService,
    DataFormattingService,
    DiscovererFacetTranslationService,
    DiscovererQueryService,
    EsQueryService,
    IColumnSetting,
    IDRFilter,
    TabSettingsService
} from '@app/discoverer/core';
import { combineLatest, Subject, Subscription } from 'rxjs';
import { DiscoReportContext, DiscoTabContext } from '@app/discoverer/dynamic-reports';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { ChartTransformService, TransformOptions, MetricCfg } from './chart-transform.service';
import { ChartConfigVM, ChartSeriesConfig } from '@app/shared-components/chart.interfaces';
import { FilterCreationService } from '../filter-creation-service';

@Component({
    selector: 'home-page-chart',
    templateUrl: './home-chart.component.html',
    styleUrls: ['./home-chart.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DynamicChartComponent implements OnInit, OnDestroy {
    public chartData: ChartSeriesConfig[] = [];
    public isChartLoading = true; // Start with loading state
    public series: ChartSeriesConfig[];
    public categories: any[]


    public readonly COLOR_PALETTES: string[] = [

        '#4caf50',
        '#0056b3',
        '#ffb300',
        '#ff6b6b',
        '#2563eb',
        '#8b5cf6',
        '#ec4899',
        '#14b8a6',
        '#f97316',
        '#a855f7',
        '#06b6d4'
    ];
    ;
    NO_DATA_COLOR = '#E0E0E0';
    @Output() chartSelected = new EventEmitter<string>();
    @Input() set extraFilters(v: Record<string, any>) {
        this._extraFilters = v || {};
        this.applyFiltersForSelected();

    }
    @Input() hideMenu = false
    @Input() chartOptionMenu: ChartConfigVM[] = [];
    @Input() scrollable: boolean = false

    @Input() set defaultRequest(v: string | undefined) {
        const requestIndex = this.chartOptionMenu.findIndex(x => x.requestId == v);
        if (requestIndex != -1) {
            const v = this.chartOptionMenu[requestIndex];
            this.selectRequest(v);
        }
    }

    public selectedRequest: ChartConfigVM | undefined;
    public reportContext: DiscoReportContext;
    public tabContext: DiscoTabContext;

    private _servicesByKey = new Map<
        string,
        { chart: ChartDataService }
    >();
    private _subsByKey = new Map<string, Subscription>();


    private _contextSub?: Subscription;

    private _extraFilters: Record<string, any> = {};
    private currentTabCols: IColumnSetting[] = [];
    tabContext$ = new Subject<DiscoTabContext>();
    reportContext$ = new Subject<DiscoReportContext>();
    constructor(
        private chartTransform: ChartTransformService,
        public formattingService: DataFormattingService,
        private facetTranslationService: DiscovererFacetTranslationService,
        private esQueryService: EsQueryService,
        private httpClient: HttpClient,
        private cdRef: ChangeDetectorRef,
        private filterCreationService: FilterCreationService

    ) { }

    ngOnInit() {

        combineLatest([this.tabContext$, this.reportContext$]).pipe(
            filter(([a, b]) => !!a && !!b),
            map(([a, b]) => ({
                tabContext: a,
                reportContext: b,
                tabKey: `${a.currentTab?.app?.key}::${a.currentTab?.key}`,
                reportKey: `${b.currentReqId}::${b.loadReqId}`,
                selectedKey: this.keyFor(this.selectedRequest)
            })),
            distinctUntilChanged((p, c) => 
                p.tabKey === c.tabKey && 
                p.reportKey === c.reportKey && 
                p.selectedKey === c.selectedKey
            ),
            map(({ tabContext, reportContext }) => [tabContext, reportContext] as const)
        ).subscribe(([tabContext, reportContext]) => {
            console.log('Context update - Tab:', tabContext.currentTab?.key, 'columns:', tabContext.columnSettings, 'Report:', reportContext.currentReqId);
            
            this.reportContext = reportContext;
            this.tabContext = tabContext;
            const key = this.keyFor(this.selectedRequest);
            const reportPersist = reportContext.reportPersistService;
            const tabSettingService = reportContext.tabSettingService;
            const tab = tabContext.currentTab;
            this.currentTabCols = tabContext.columnSettings;
            this.selectedRequest.queryService = new DiscovererQueryService(this.selectedRequest.requestId);
            this.selectedRequest.queryService.filters.push(...reportPersist.mainQueryService.filters);
            this.applyFiltersForSelected();
            const chart = new ChartDataService(
                this.httpClient,
                tab.serviceUrl,
                this.selectedRequest.queryService,
                reportPersist.chartState,
                this.formattingService,
                tabSettingService,
                this.facetTranslationService,
                this.currentTabCols.map(s => Object.assign({}, s)),
                this.esQueryService
            );
            this._servicesByKey.set(key, { chart });
            this.ensureSingleSubscriptionAndRefresh(this.selectedRequest);
        });

    }

    ngOnDestroy(): void {
        this.teardownAllServices();
        this._contextSub?.unsubscribe();
    }


    selectRequest(option: ChartConfigVM) {

        if (this.selectedRequest?.requestId === option.requestId) {
            return;
        }

        this.selectedRequest = option;
        this.isChartLoading = true;
        this.cdRef.detectChanges();

        this.chartSelected.emit(option.requestId);
    }

    setTabContext(context: DiscoTabContext): void {
        if (context && context.columnSettings) {

            this.tabContext$.next(context)
        }
        this.tabContext = this.tabContext
    }
    get useRightAxis(): boolean {
        return (this.series ?? []).some(s => s.axis === 'valueAxis');
    }
    get isDonutChart(): boolean {
        return this.series?.some(s => s.type === 'donut') ?? false;
    }

    async setReportContext(context: DiscoReportContext) {
        if (context && context.tabSettingService) {
            this.reportContext$.next(context)
        }
    }


    private ensureSingleSubscriptionAndRefresh(req: ChartConfigVM) {
        const key = this.keyFor(req);
        const svc = this._servicesByKey.get(key);
        if (!svc) return;

        const existingSub = this._subsByKey.get(key);
        if (existingSub) {
            existingSub.unsubscribe();
        }

        this.isChartLoading = true;
        this.cdRef.detectChanges();

        const sub = svc.chart.oData.subscribe(x => {
            this.handleDataEmission(x);
        });
        this._subsByKey.set(key, sub);
    }


    private applyFiltersForSelected() {
        if (!this.selectedRequest || !this.selectedRequest?.filterMap) return;
        const filters = this._extraFilters || {};

        for (const [field, value] of Object.entries(filters)) {
            if (value === undefined || value === null || value === '') continue;

            const mappedField =
                this.selectedRequest.filterMap?.[field] ?? field;
            const filter = this.filterCreationService.createFilter(mappedField, value, this.currentTabCols);
            this.selectedRequest.queryService.setFilter(mappedField, filter);
            this.selectedRequest.queryService.refresh()
        }
    }
    private handleDataEmission = (x: any) => {
        const metricsCfg: MetricCfg[] = Array.isArray(this.selectedRequest?.metric)
            ? this.selectedRequest!.metric
            : [];

        const opts: TransformOptions = {
            isTimeCategory: !!this.selectedRequest?.isTimeCategory,
            isStack: !!this.selectedRequest?.isStack,
            metricsCfg,
            seriesList: Array.isArray(x?.series) ? x.series : [],
            colorPalette: this.COLOR_PALETTES,
            noDataColor: this.NO_DATA_COLOR,
            donutTotalFromBlock: true,
            timeWindowDays: 7,
        };

        const { categories, series } = this.chartTransform.transform(
            { categories: x?.categories, values: x?.values, series: x?.series },
            opts
        );

        this.categories = categories;
        this.series = series as any;

        this.isChartLoading = false;
        this.cdRef.detectChanges();
    };


    private keyFor(req: ChartConfigVM): string {
        const idLike = req.requestId;
        return `${req.tabId}::${idLike || 'default'}`;
    }



    private teardownAllServices(): void {
        for (const sub of this._subsByKey.values()) sub.unsubscribe();
        this._subsByKey.clear();
        this._servicesByKey.clear();
    }


}
